import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { shipmentsAPI, customersAPI, citiesAPI } from '../services/api';
import { ArrowLeft, Save, X, Plus, Trash2, MapPin, Paperclip } from 'lucide-react';
import toast from 'react-hot-toast';
import AttachmentUpload from '../components/AttachmentUpload';
import CityDropdown from '../components/CityDropdown';
import CustomerSearchDropdown from '../components/CustomerSearchDropdown';

const EditShipment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    customerId: '',
    pickupAddress: '',
    pickupLocation: '',
    deliveryAddress: '',
    cityId: '',
    dueDate: '',
    deliveryDate: '',
    description: '',
    status: 'inquiry',
    shipmentPaymentStatus: 'pending'
  });
  const [boxRequirements, setBoxRequirements] = useState([
    { boxType: '', quantity: 1, customType: '' }
  ]);
  const [errors, setErrors] = useState({});
  const [showAttachments, setShowAttachments] = useState(false);

  // Box type options
  const boxTypeOptions = [
    { value: 'jumbo', label: 'Jumbo' },
    { value: 'medium', label: 'Medium' },
    { value: 'small', label: 'Small' },
    { value: 'custom', label: 'Custom' }
  ];

  // Status options
  const statusOptions = [
    { value: 'inquiry', label: 'Inquiry' },
    { value: 'box_collection', label: 'Box Collection' },
    { value: 'box_collection_done', label: 'Box Collection Done' },
    { value: 'box_delivery', label: 'Box Delivery' },
    { value: 'box_delivery_done', label: 'Box Delivery Done' },
    { value: 'quick_packing', label: 'Quick Packing' },
    { value: 'quick_packing_done', label: 'Quick Packing Done' },
    { value: 'shipment', label: 'Ready for Shipment' }
  ];

  // Shipment status options
  const shipmentStatusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in_shipping', label: 'In Shipping' },
    { value: 'in_sl_warehouse', label: 'In SL Warehouse' },
    { value: 'shipping_to_customer', label: 'Shipping to Customer' },
    { value: 'delivered', label: 'Delivered' }
  ];

  // Payment status options
  const paymentStatusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'partial_paid', label: 'Partial Paid' },
    { value: 'fully_paid', label: 'Fully Paid' }
  ];

  // Fetch customers for dropdown
  const { data: customersData } = useQuery('customers', () => customersAPI.getAll());
  const customers = customersData?.customers || [];



  // Fetch shipment data
  const { data: shipmentData, isLoading } = useQuery(['shipment', id], () => shipmentsAPI.getById(id), {
    onSuccess: (data) => {
      const { shipment, boxRequirements, attachments } = data;
      
      // Properly format the dates for the input fields - preserve local timezone
      let formattedDueDate = '';
      if (shipment.due_date) {
        // Handle both ISO string and date object formats
        const dueDate = new Date(shipment.due_date);
        if (!isNaN(dueDate.getTime())) {
          // Format as YYYY-MM-DD preserving local timezone
          const year = dueDate.getFullYear();
          const month = String(dueDate.getMonth() + 1).padStart(2, '0');
          const day = String(dueDate.getDate()).padStart(2, '0');
          formattedDueDate = `${year}-${month}-${day}`;
        }
      }

      let formattedDeliveryDate = '';
      if (shipment.delivery_date) {
        const deliveryDate = new Date(shipment.delivery_date);
        if (!isNaN(deliveryDate.getTime())) {
          const year = deliveryDate.getFullYear();
          const month = String(deliveryDate.getMonth() + 1).padStart(2, '0');
          const day = String(deliveryDate.getDate()).padStart(2, '0');
          formattedDeliveryDate = `${year}-${month}-${day}`;
        }
      }
      
      setFormData({
        customerId: shipment.customer_id,
        pickupAddress: shipment.pickup_address,
        pickupLocation: shipment.pickup_location || '',
        deliveryAddress: shipment.delivery_address,
        cityId: shipment.city_id,
        dueDate: formattedDueDate,
        deliveryDate: formattedDeliveryDate,
        description: shipment.description || '',
        status: shipment.status || 'inquiry',
        shipmentPaymentStatus: shipment.shipment_payment_status || 'pending'
      });
      setBoxRequirements(
        boxRequirements.map(box => ({
          boxType: ['jumbo', 'medium', 'small'].includes(box.box_type) ? box.box_type : 'custom',
          customType: ['jumbo', 'medium', 'small'].includes(box.box_type) ? '' : box.box_type,
          quantity: box.quantity
        }))
      );
    }
  });

  const updateShipmentMutation = useMutation(
    (data) => shipmentsAPI.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['shipment', id]);
        queryClient.invalidateQueries('shipments');
        toast.success('Shipment updated successfully!');
        navigate(`/shipments/${id}`);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update shipment');
      }
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleBoxRequirementChange = (index, field, value) => {
    const updatedBoxRequirements = [...boxRequirements];
    updatedBoxRequirements[index][field] = value;
    if (field === 'boxType' && value !== 'custom') {
      updatedBoxRequirements[index].customType = '';
    }
    setBoxRequirements(updatedBoxRequirements);
  };

  const addBoxRequirement = () => {
    setBoxRequirements([...boxRequirements, { boxType: '', quantity: 1, customType: '' }]);
  };

  const removeBoxRequirement = (index) => {
    if (boxRequirements.length > 1) {
      setBoxRequirements(boxRequirements.filter((_, i) => i !== index));
    }
  };

  // Handle pickup location input - extract coordinates from URLs
  const handlePickupLocationChange = (e) => {
    let value = e.target.value.trim();

    // If it's a Google Maps search URL wrapping another URL, extract the query param
    const searchUrlMatch = value.match(/google\.com\/maps\/search\/\?api=1&query=([^&]+)/);
    if (searchUrlMatch) {
      // Decode the inner query
      value = decodeURIComponent(searchUrlMatch[1]);
    }

    // If it's a Google Maps URL, try to extract coordinates
    if (value.includes('maps.google.com') || value.includes('maps.app.goo.gl')) {
      // Extract coordinates from URL (basic implementation)
      const coordMatch = value.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (coordMatch) {
        const lat = coordMatch[1];
        const lng = coordMatch[2];
        setFormData(prev => ({
          ...prev,
          pickupLocation: `${lat}, ${lng}`
        }));
        return;
      }
      // If no coordinates, just save the URL as-is
      setFormData(prev => ({
        ...prev,
        pickupLocation: value
      }));
      return;
    }

    // Otherwise, just save the value (could be coordinates or address)
    setFormData(prev => ({
      ...prev,
      pickupLocation: value
    }));
  };

  // Utility to normalize pickup location
  function normalizePickupLocation(value) {
    if (!value) return '';
    let loc = value.trim();
    if (loc.startsWith('@')) loc = loc.slice(1);

    // If it's a Google Maps search URL, extract the query param
    const searchUrlMatch = loc.match(/google\.com\/maps\/search\/\?api=1&query=([^&]+)/);
    if (searchUrlMatch) {
      loc = decodeURIComponent(searchUrlMatch[1]);
      if (loc.startsWith('@')) loc = loc.slice(1);
    }
    return loc;
  }

  const validateForm = () => {
    const newErrors = {};
    if (!formData.customerId) newErrors.customerId = 'Customer is required';
    if (!formData.cityId) newErrors.cityId = 'City is required';
    if (!formData.dueDate) newErrors.dueDate = 'Due date is required';

    // Validate delivery date cannot be after due date
    if (formData.deliveryDate && formData.dueDate) {
      const deliveryDate = new Date(formData.deliveryDate);
      const dueDate = new Date(formData.dueDate);
      if (deliveryDate > dueDate) {
        newErrors.deliveryDate = 'Delivery date cannot be after due date';
      }
    }

    // Validate box requirements
    const boxErrors = [];
    boxRequirements.forEach((box, index) => {
      if (!box.boxType) boxErrors[index] = { ...boxErrors[index], boxType: 'Box type is required' };
      if (box.boxType === 'custom' && !box.customType.trim()) boxErrors[index] = { ...boxErrors[index], customType: 'Custom box type is required' };
      if (!box.quantity || box.quantity < 1) boxErrors[index] = { ...boxErrors[index], quantity: 'Quantity must be at least 1' };
    });
    if (boxErrors.length > 0) newErrors.boxRequirements = boxErrors;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    updateShipmentMutation.mutate({
      customerId: parseInt(formData.customerId),
      pickupAddress: formData.pickupAddress.trim(),
      pickupLocation: normalizePickupLocation(formData.pickupLocation) || null,
      deliveryAddress: formData.deliveryAddress.trim(),
      cityId: parseInt(formData.cityId),
      dueDate: formData.dueDate,
      deliveryDate: formData.deliveryDate || null,
      description: formData.description,
      boxRequirements: boxRequirements.map(box => ({
        boxType: box.boxType === 'custom' ? box.customType.trim() : box.boxType,
        quantity: parseInt(box.quantity)
      })),
      status: formData.status,
      shipment_payment_status: formData.shipmentPaymentStatus
    });
  };

  const handleCancel = () => {
    navigate(`/shipments/${id}`);
  };

  const handleAttachmentsSuccess = () => {
    // Refresh the shipment data to get updated attachments
    queryClient.invalidateQueries(['shipment', id]);
  };

  if (isLoading) return <div className="p-8 text-center">Loading...</div>;

  const { shipment, attachments } = shipmentData || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(`/shipments/${id}`)}
          className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Shipment
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Edit Shipment #{id}</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Edit Shipment Details</h3>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Customer */}
                <div>
                  <label htmlFor="customerId" className="block text-sm font-medium text-gray-700 mb-2">
                    Customer *
                  </label>
                  <CustomerSearchDropdown
                    value={formData.customerId}
                    onChange={customerId => setFormData(prev => ({ ...prev, customerId }))}
                    placeholder="Select Customer"
                    error={!!errors.customerId}
                  />
                  {errors.customerId && (
                    <p className="mt-1 text-sm text-red-600">{errors.customerId}</p>
                  )}
                </div>
                {/* City */}
                <div>
                  <label htmlFor="cityId" className="block text-sm font-medium text-gray-700 mb-2">
                    City *
                  </label>
                  <CityDropdown
                    value={formData.cityId}
                    onChange={(cityId) => setFormData(prev => ({ ...prev, cityId }))}
                    placeholder="Select City"
                    error={!!errors.cityId}
                  />
                  {errors.cityId && (
                    <p className="mt-1 text-sm text-red-600">{errors.cityId}</p>
                  )}
                </div>
                {/* Pickup Address */}
                <div className="md:col-span-2">
                  <label htmlFor="pickupAddress" className="block text-sm font-medium text-gray-700 mb-2">
                    Pickup Address
                  </label>
                  <textarea
                    id="pickupAddress"
                    name="pickupAddress"
                    value={formData.pickupAddress}
                    onChange={handleChange}
                    rows={2}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.pickupAddress ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter pickup address"
                  />
                  {errors.pickupAddress && (
                    <p className="mt-1 text-sm text-red-600">{errors.pickupAddress}</p>
                  )}
                </div>
                {/* Pickup Location (Google Maps) */}
                <div className="md:col-span-2">
                  <label htmlFor="pickupLocation" className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin className="inline h-4 w-4 mr-1" />
                    Pickup Location (Google Maps)
                  </label>
                  <input
                    type="text"
                    id="pickupLocation"
                    name="pickupLocation"
                    value={formData.pickupLocation}
                    onChange={handlePickupLocationChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter coordinates (e.g., 23.5880, 58.3829) or Google Maps URL"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Enter coordinates (latitude, longitude) or paste a Google Maps URL to auto-extract coordinates
                  </p>
                </div>
                {/* Delivery Address */}
                <div className="md:col-span-2">
                  <label htmlFor="deliveryAddress" className="block text-sm font-medium text-gray-700 mb-2">
                    Delivery Address
                  </label>
                  <textarea
                    id="deliveryAddress"
                    name="deliveryAddress"
                    value={formData.deliveryAddress}
                    onChange={handleChange}
                    rows={2}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.deliveryAddress ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter delivery address"
                  />
                  {errors.deliveryAddress && (
                    <p className="mt-1 text-sm text-red-600">{errors.deliveryAddress}</p>
                  )}
                </div>
                {/* Delivery Date */}
                <div>
                  <label htmlFor="deliveryDate" className="block text-sm font-medium text-gray-700 mb-2">
                    Delivery Date
                  </label>
                  <input
                    type="date"
                    id="deliveryDate"
                    name="deliveryDate"
                    value={formData.deliveryDate}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.deliveryDate ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.deliveryDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.deliveryDate}</p>
                  )}
                </div>

                {/* Due Date */}
                <div>
                  <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 mb-2">
                    Due Date *
                  </label>
                  <input
                    type="date"
                    id="dueDate"
                    name="dueDate"
                    value={formData.dueDate}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.dueDate ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.dueDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.dueDate}</p>
                  )}
                </div>

                {/* Status */}
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Payment Status */}
                <div>
                  <label htmlFor="shipmentPaymentStatus" className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Status
                  </label>
                  <select
                    id="shipmentPaymentStatus"
                    name="shipmentPaymentStatus"
                    value={formData.shipmentPaymentStatus}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {paymentStatusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter shipment description (optional)"
                  />
                </div>
              </div>
              {/* Box Requirements */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-gray-900">Box Requirements *</h4>
                  <button
                    type="button"
                    onClick={addBoxRequirement}
                    className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Box
                  </button>
                </div>
                <div className="space-y-4">
                  {boxRequirements.map((box, index) => (
                    <div key={index} className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Box Type *
                        </label>
                        <select
                          value={box.boxType}
                          onChange={(e) => handleBoxRequirementChange(index, 'boxType', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            errors.boxRequirements?.[index]?.boxType ? 'border-red-500' : 'border-gray-300'
                          }`}
                        >
                          <option value="">Select Box Type</option>
                          {boxTypeOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        {errors.boxRequirements?.[index]?.boxType && (
                          <p className="mt-1 text-sm text-red-600">{errors.boxRequirements[index].boxType}</p>
                        )}
                      </div>
                      {box.boxType === 'custom' && (
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Custom Box Type *
                          </label>
                          <input
                            type="text"
                            value={box.customType}
                            onChange={(e) => handleBoxRequirementChange(index, 'customType', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                              errors.boxRequirements?.[index]?.customType ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="Enter custom box type"
                          />
                          {errors.boxRequirements?.[index]?.customType && (
                            <p className="mt-1 text-sm text-red-600">{errors.boxRequirements[index].customType}</p>
                          )}
                        </div>
                      )}
                      <div className="w-24">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Quantity *
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={box.quantity}
                          onChange={(e) => handleBoxRequirementChange(index, 'quantity', parseInt(e.target.value) || 1)}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            errors.boxRequirements?.[index]?.quantity ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {errors.boxRequirements?.[index]?.quantity && (
                          <p className="mt-1 text-sm text-red-600">{errors.boxRequirements[index].quantity}</p>
                        )}
                      </div>
                      {boxRequirements.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeBoxRequirement(index)}
                          className="mt-6 p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              {/* Form Actions */}
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateShipmentMutation.isLoading}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {updateShipmentMutation.isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Updating...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Update Shipment
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Attachments Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Paperclip className="h-5 w-5 mr-2" />
                Attachments
              </h3>
            </div>
            <div className="p-6">
              <AttachmentUpload 
                shipmentId={id}
                attachments={attachments || []}
                onUploadSuccess={handleAttachmentsSuccess}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditShipment; 